import type { Ref } from 'vue'
import type { NativeSelectOption } from '@shared/ui/fields'

import { Discipline } from '@brighterly/lib-core-types/discipline/enums'

import { useListsStore } from '@shared/model'
import { resolveUrl } from '@shared/lib/url'
import { AppRoute } from '@shared/config'

import { DisciplineGeneralModal } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineGeneralInfoStore } from './general-info/store'
import { useDisciplineDuplicateApi } from '../api/duplicate.hook'
import { getDisciplineName } from './discipline-general-info'

interface DisciplineModalDuplicateReturn {
  refIsOpen: ComputedRef<boolean>
  refIsLoading: Ref<boolean>
  refTitle: ComputedRef<string>
  refOptions: ComputedRef<NativeSelectOption[]>
  refCode: Ref<Discipline>
  setCode: (option: NativeSelectOption) => void
  closeModal: () => void
  confirmDuplicate: () => Promise<void>
}

export const useDisciplineModalDuplicate = (): DisciplineModalDuplicateReturn => {
  const disciplineStore = useDisciplineStore()
  const generalInfoStore = useDisciplineGeneralInfoStore()
  const listsStore = useListsStore()

  const refIsLoading = ref(false)
  const refCode = ref<Discipline>(Discipline.ELA)

  const refIsOpen = computed<boolean>(() => generalInfoStore.getActiveModal === DisciplineGeneralModal.Duplicate)

  const refTitle = computed<string>(() => {
    const code = disciplineStore.getDetails?.code ?? ''
    return `Duplicate discipline / ${getDisciplineName(code, listsStore.getLists?.discipline.code)}`
  })

  const refOptions = computed<NativeSelectOption[]>(() =>
    Object.entries(listsStore.getLists?.discipline.code ?? {}).map(([value, text]) => ({
      id: value,
      value,
      text,
      selected: value === refCode.value,
    })))

  const setCode = (option: NativeSelectOption): void => {
    refCode.value = option.value as Discipline
  }

  const closeModal = (): void => {
    generalInfoStore.setActiveModal(null)
  }

  const confirmDuplicate = async (): Promise<void> => {
    const details = disciplineStore.getDetails
    if (!details || refIsLoading.value) return

    const duplicate = await useDisciplineDuplicateApi({
      disciplineId: details.id,
      code: refCode.value,
      onStart: () => { refIsLoading.value = true },
      onFinish: () => { refIsLoading.value = false },
    })

    closeModal()

    const duplicateUrl = duplicate ? resolveUrl(AppRoute.DisciplinesGeneralInfo, { id: duplicate.id }) : null
    if (duplicateUrl) await navigateTo(duplicateUrl)
  }

  watch(refIsOpen, (isOpen) => {
    if (isOpen) refCode.value = Discipline.ELA
  })

  return {
    refIsOpen,
    refIsLoading,
    refTitle,
    refOptions,
    refCode,
    setCode,
    closeModal,
    confirmDuplicate,
  }
}
